import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  PropsWithChildren,
} from "react";
import { io, Socket } from "socket.io-client";
import { WS_HOST } from "../util/constants";

interface ContextProps {
  socket: Socket | null;
  connected: boolean;
  appSettings: any | null;
  setAppSettings: (settings: any) => void;
}

const AppSocketContext = createContext<ContextProps | undefined>(undefined);

const HEARTBEAT_MS = 5000;
const SETTINGS_POLL_MS = 5000;

const settingsUnchanged = (prev: any, next: any): boolean => {
  if (prev === next) return true;
  if (prev == null || next == null) return prev == null && next == null;
  try {
    return JSON.stringify(prev) === JSON.stringify(next);
  } catch {
    return false;
  }
};

export const AppSocketProvider = ({ children }: PropsWithChildren) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [appSettings, setAppSettings] = useState<any | null>(null);
  const [connected, setConnected] = useState(false);

  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const settingsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!WS_HOST) {
      console.error("WS_HOST is not configured; live settings/access grants disabled");
      return;
    }

    const clearIntervals = () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (settingsIntervalRef.current) {
        clearInterval(settingsIntervalRef.current);
        settingsIntervalRef.current = null;
      }
    };

    const startIntervals = (s: Socket) => {
      clearIntervals();
      // Immediate fetch on connect / reconnect — do not wait for first interval tick.
      s.emit("get_app_settings");
      s.emit("heartbeat");

      heartbeatIntervalRef.current = setInterval(() => {
        s.emit("heartbeat");
      }, HEARTBEAT_MS);

      settingsIntervalRef.current = setInterval(() => {
        s.emit("get_app_settings");
      }, SETTINGS_POLL_MS);
    };

    const s = io(WS_HOST, {
      autoConnect: true,
      reconnection: true,
    });
    setSocket(s);

    const onConnect = () => {
      console.info("AppSocket connected");
      setConnected(true);
      startIntervals(s);
    };

    const onDisconnect = () => {
      console.warn("AppSocket disconnected");
      setConnected(false);
      clearIntervals();
    };

    const onHeartbeatAck = (data: any) => {
      if (data && data.success === false) {
        console.warn("Heartbeat not acknowledged, disconnecting...");
        setConnected(false);
        s.disconnect();
      } else {
        setConnected(true);
      }
    };

    const onConnectError = (error: Error) => {
      console.error("WebSocket connection error:", error);
      setConnected(false);
    };

    const onSettingsAck = (data: any) => {
      if (data?.status === "error") {
        console.warn("App settings fetch failed:", data?.message);
        return;
      }
      const next = data?.data ?? null;
      setAppSettings((prev: any) =>
        settingsUnchanged(prev, next) ? prev : next
      );
    };

    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    s.on("heartbeat_ack", onHeartbeatAck);
    s.on("connect_error", onConnectError);
    s.on("get_app_settings_ack", onSettingsAck);

    // Socket.IO may already be connected by the time handlers are attached.
    if (s.connected) {
      onConnect();
    }

    return () => {
      clearIntervals();
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      s.off("heartbeat_ack", onHeartbeatAck);
      s.off("connect_error", onConnectError);
      s.off("get_app_settings_ack", onSettingsAck);
      s.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, []);

  return (
    <AppSocketContext.Provider
      value={{
        socket,
        connected,
        appSettings,
        setAppSettings,
      }}
    >
      {children}
    </AppSocketContext.Provider>
  );
};

export const useAppSocket = (): ContextProps => {
  const context = useContext(AppSocketContext);
  if (context === undefined) {
    throw new Error("useAppSocket must be used within a AppSocketProvider");
  }
  return context;
};
